// src/components/principal/StaffManager.jsx (v5 – final, warning-free)

import React, { useEffect, useRef, useState } from "react";
import Papa from "papaparse";
import * as XLSX from "xlsx";

import api from "../../services/api";
import { API_PATHS, getAssetUrl } from "../../config";

/**
 * Staff management panel for principals.
 *
 * Lists current staff, allows bulk creation via text input or CSV/XLSX upload,
 * and supports revoking access.
 *
 * @component
 * @param {Object} props
 * @param {function} props.onError - Error reporting callback
 * @returns {JSX.Element}
 */
const StaffManager = ({ onError }) => {
  const [staff, setStaff] = useState([]);
  const [textEmails, setTextEmails] = useState("");
  const [file, setFile] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmEmails, setConfirmEmails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const fileRef = useRef(null);

  const validateEmail = (email) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const extractEmailsFromFile = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const data = event.target.result;
          if (file.name.endsWith(".csv")) {
            Papa.parse(data, {
              complete: (results) => {
                const emails = results.data
                  .flat()
                  .map((e) => String(e).trim())
                  .filter(validateEmail);
                const unique = [...new Set(emails.map((e) => e.toLowerCase()))]
                  .map((e) => emails.find((g) => g.toLowerCase() === e));
                resolve(unique);
              },
              error: reject,
            });
          } else if (file.name.endsWith(".xlsx")) {
            const wb = XLSX.read(data, { type: "binary" });
            const sheet = wb.Sheets[wb.SheetNames[0]];
            const json = XLSX.utils.sheet_to_json(sheet, { header: 1 });
            const emails = json
              .flat()
              .map((e) => String(e).trim())
              .filter(validateEmail);
            const unique = [...new Set(emails.map((e) => e.toLowerCase()))]
              .map((e) => emails.find((g) => g.toLowerCase() === e));
            resolve(unique);
          } else {
            reject(new Error("Unsupported file format."));
          }
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = () => reject(new Error("Failed to read file."));
      file.name.endsWith(".xlsx")
        ? reader.readAsBinaryString(file)
        : reader.readAsText(file);
    });

  // Load staff list on mount
  useEffect(() => {
    const fetchStaff = async () => {
      try {
        const { data } = await api.get(API_PATHS.STAFF_LIST);
        setStaff(Array.isArray(data) ? data : []);
      } catch {
        onError?.("Failed to load staff list.");
      }
    };

    fetchStaff();
  }, [onError]);

  const onChooseFile = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (!f.name.match(/\.(csv|xlsx)$/i)) {
      onError?.("Please upload a CSV or XLSX file.");
      if (fileRef.current) fileRef.current.value = null;
      setFile(null);
      return;
    }
    setFile(f);
  };

  const startCreate = async (e) => {
    e.preventDefault();
    setSuccessMessage("");
    setLoading(true);
    try {
      let valid = [];
      if (file) {
        valid = await extractEmailsFromFile(file);
      } else {
        const raw = textEmails
          .split(/[\s,;]+/)
          .map((e) => e.trim());
        const deduped = [...new Set(raw.map((e) => e.toLowerCase()))]
          .map((e) => raw.find((g) => g.toLowerCase() === e));
        valid = deduped.filter(validateEmail);
        const invalid = raw.filter((e) => e && !validateEmail(e));
        if (invalid.length) {
          onError?.(`Invalid email(s): ${invalid.join(", ")}`);
          return;
        }
      }

      if (!valid.length) {
        onError?.("Please enter at least one valid email or upload a file.");
        return;
      }

      setConfirmEmails(valid);
      setShowConfirm(true);
    } catch (err) {
      onError?.(err.message || "Failed to process emails.");
    } finally {
      setLoading(false);
    }
  };

  const confirmCreate = async () => {
    setLoading(true);
    setShowConfirm(false);
    try {
      let res;
      if (file) {
        const formData = new FormData();
        formData.append("file", file);
        res = await api.post(API_PATHS.CREATE_STAFF_UPLOAD, formData);
      } else {
        res = await api.post(API_PATHS.CREATE_STAFF, confirmEmails.map((email) => ({ email })));
      }

      const msg = res.data?.message || "Staff accounts processed successfully.";
      const failed = res.data?.failed || [];
      if (failed.length) {
        onError?.(`${msg} Failed: ${failed.join(", ")}`);
      } else {
        setSuccessMessage(msg);
      }

      // Refresh staff list
      try {
        const { data } = await api.get(API_PATHS.STAFF_LIST);
        setStaff(Array.isArray(data) ? data : []);
      } catch {
        // Silent — success message already shown
      }

      setFile(null);
      setTextEmails("");
      if (fileRef.current) fileRef.current.value = null;
    } catch (e) {
      onError?.(e.response?.data?.detail || "Failed to create staff accounts.");
    } finally {
      setLoading(false);
    }
  };

  const revoke = async (id, email) => {
    if (!window.confirm(`Deactivate ${email}? They will lose access.`)) return;
    try {
      await api.delete(API_PATHS.REVOKE_ACCESS(email));
      setStaff((prev) => prev.filter((s) => s.id !== id));
    } catch (e) {
      onError?.(e.response?.data?.detail || `Failed to deactivate ${email}.`);
    }
  };

  const csvTemplate = getAssetUrl("static/templates/staff_emails_template.csv");
  const xlsxTemplate = getAssetUrl("static/templates/staff_emails_template.xlsx");

  return (
    <div className="principal-panel staff-content">
      <h3>Current Staff</h3>

      {!staff.length ? (
        <p className="no-staff-message">No Staff Accounts created yet</p>
      ) : (
        <div className="staff-table-wrap" role="region" aria-label="Current staff list">
          <table className="staff-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Status</th>
                <th className="actions-col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {staff.map((s) => {
                const status = String(s.status || "UNKNOWN").toUpperCase();
                const isActive = status === "ACTIVE";
                const displayName = s.display_name || s.email;
                const isDefaultName = !s.display_name;

                return (
                  <tr key={s.id || s.email} className="staff-row">
                    <td className={isDefaultName ? "is-default-name" : ""}>
                      {displayName}
                    </td>
                    <td>{s.email}</td>
                    <td>
                      <span className={`status-pill ${isActive ? "is-active" : "is-inactive"}`}>
                        {status}
                      </span>
                    </td>
                    <td>
                      <button
                        className="btn btn-outline btn-danger compact"
                        onClick={() => revoke(s.id, s.email)}
                      >
                        Revoke
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <h3>Create New Staff</h3>
      <form onSubmit={startCreate} className="staff-create-form">
        <textarea
          value={textEmails}
          onChange={(e) => setTextEmails(e.target.value)}
          placeholder="Type or paste staff emails (comma/space separated) or upload a file below."
          rows={6}
          className="staff-emails-textarea"
        />

        <button type="submit" disabled={loading} className="btn btn-primary staff-create-button">
          {loading ? "Processing…" : "Create Staff"}
        </button>

        <div className="staff-file-row">
          <p className="staff-upload-hint">
            Or upload a CSV/Excel file with emails.
          </p>
          <div className="staff-file-input-wrapper">
            <button
              type="button"
              className="btn btn-outline staff-file-button"
              onClick={() => fileRef.current?.click()}
              disabled={loading}
            >
              {file ? "Change file" : "Upload CSV or Excel"}
            </button>
            <span className="staff-file-name">{file ? file.name : "No file selected"}</span>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,.xlsx"
              onChange={onChooseFile}
              className="staff-file-input-hidden"
            />
          </div>
        </div>
      </form>

      {successMessage && <p className="success-message">{successMessage}</p>}

      {showConfirm && (
        <div className="confirmation-dialog">
          <p>Confirm creation for:</p>
          <ul>
            {confirmEmails.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
          <div className="confirmation-actions">
            <button type="button" onClick={confirmCreate} className="btn btn-primary">
              Proceed
            </button>
            <button type="button" onClick={() => setShowConfirm(false)} className="btn btn-outline">
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="template-downloads">
        <ul>
          <li>
            <a href={csvTemplate} download className="template-download-link">
              Download CSV Template
            </a>
          </li>
          <li>
            <a href={xlsxTemplate} download className="template-download-link">
              Download Excel Template
            </a>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default StaffManager;